export interface RoutineSchedule {
  cadence: 'daily' | 'weekdays' | 'weekly' | 'monthly';
  timeOfDay: string;
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
}

export interface RoutineNameParts {
  before: string;
  after?: string;
  precededByAt: boolean;
}

export interface RoutineForJobState {
  id: string;
  active: boolean;
  schedule: RoutineSchedule;
  originalSchedule?: RoutineSchedule | null;
}

export interface JobForCustomisation {
  customized: boolean;
  activeGeneration: number;
}

export const SCHEDULE_OPTIONS: Record<
  RoutineSchedule['cadence'],
  RoutineSchedule[]
> = {
  daily: [
    { cadence: 'daily', timeOfDay: '08:00' },
    { cadence: 'daily', timeOfDay: '12:00' },
    { cadence: 'daily', timeOfDay: '17:00' },
  ],
  weekdays: [
    { cadence: 'weekdays', timeOfDay: '08:00' },
    { cadence: 'weekdays', timeOfDay: '09:00' },
    { cadence: 'weekdays', timeOfDay: '17:00' },
  ],
  weekly: [
    { cadence: 'weekly', timeOfDay: '09:00', dayOfWeek: 1 },
    { cadence: 'weekly', timeOfDay: '17:00', dayOfWeek: 5 },
    { cadence: 'weekly', timeOfDay: '18:00', dayOfWeek: 0 },
  ],
  monthly: [
    { cadence: 'monthly', timeOfDay: '09:00', dayOfMonth: 1 },
    { cadence: 'monthly', timeOfDay: '09:00', dayOfMonth: 15 },
    { cadence: 'monthly', timeOfDay: '09:00', dayOfMonth: 28 },
  ],
};

const DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const scheduleTimeBucket = (
  timeOfDay: string,
): 'morning' | 'afternoon' | 'evening' => {
  const hour = Number.parseInt(timeOfDay.slice(0, 2), 10);
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
};

const scheduleTimePhrase = (timeOfDay: string): string => {
  const match = /^(\d{2}):(\d{2})$/.exec(timeOfDay);
  if (!match) return timeOfDay;
  const hour = Number(match[1]);
  const minute = match[2];
  const meridiem = hour < 12 ? 'am' : 'pm';
  const displayHour = hour % 12 || 12;
  return `${displayHour}${minute === '00' ? '' : `:${minute}`}${meridiem}`;
};

const ordinal = (day: number): string => {
  const suffix =
    day % 100 >= 11 && day % 100 <= 13
      ? 'th'
      : (({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[day % 10] ??
        'th');
  return `${day}${suffix}`;
};

export const formatSchedule = (
  schedule: RoutineSchedule,
  precededByAt = false,
): string => {
  if (precededByAt) return scheduleTimePhrase(schedule.timeOfDay);
  const bucket = scheduleTimeBucket(schedule.timeOfDay);
  if (schedule.cadence === 'daily') return `every ${bucket}`;
  if (schedule.cadence === 'weekdays') return `weekday ${bucket}s`;
  if (schedule.cadence === 'weekly') {
    return `${DAYS[schedule.dayOfWeek ?? 1]} ${bucket}s`;
  }
  return `the ${ordinal(schedule.dayOfMonth ?? 1)} of each month`;
};

export const splitRoutineName = (name: string): RoutineNameParts => {
  const placeholderIndex = name.indexOf('{schedule}');
  if (placeholderIndex === -1) {
    return { before: name, precededByAt: false };
  }
  const before = name.slice(0, placeholderIndex);
  return {
    before,
    after: name.slice(placeholderIndex + '{schedule}'.length),
    precededByAt: before.endsWith('at '),
  };
};

export const routineNameWithSchedule = (
  name: string,
  schedule: RoutineSchedule,
): string => {
  const parts = splitRoutineName(name);
  const phrase = formatSchedule(schedule, parts.precededByAt);
  if (parts.after === undefined) return `${name} ${phrase}`;
  return `${parts.before}${phrase}${parts.after}`;
};

export const sameSchedule = (
  first: RoutineSchedule,
  second: RoutineSchedule,
): boolean =>
  first.cadence === second.cadence &&
  first.timeOfDay === second.timeOfDay &&
  (first.dayOfWeek ?? null) === (second.dayOfWeek ?? null) &&
  (first.dayOfMonth ?? null) === (second.dayOfMonth ?? null);

export const scheduleOptionsFor = (
  routine: Pick<RoutineForJobState, 'schedule'>,
  precededByAt = false,
): RoutineSchedule[] => {
  const labels = new Set<string>();
  const options = SCHEDULE_OPTIONS[routine.schedule.cadence].filter(
    (option) => {
      const label = formatSchedule(option, precededByAt);
      if (labels.has(label)) return false;
      labels.add(label);
      return true;
    },
  );
  const currentLabel = formatSchedule(routine.schedule, precededByAt);
  return options.some(
    (option) => formatSchedule(option, precededByAt) === currentLabel,
  )
    ? options
    : [...options, routine.schedule];
};

export const routineUsesClockPhrase = (name: string): boolean =>
  splitRoutineName(name).precededByAt;

export const isJobOn = (
  routines: ReadonlyArray<Pick<RoutineForJobState, 'id' | 'active'>>,
  routineState: Readonly<Record<string, boolean>>,
): boolean =>
  routines.some((routine) => routineState[routine.id] ?? routine.active);

export const INTEGRATION_POLLING_MAX_DURATION_MS = 5 * 60 * 1000;
export const INTEGRATION_POLLING_EARLY_WINDOW_MS = 30 * 1000;
export const INTEGRATION_POLLING_INITIAL_INTERVAL_MS = 2 * 1000;
export const INTEGRATION_POLLING_INTERVAL_MS = 5 * 1000;

export const shouldContinueIntegrationPolling = (
  response: {
    understanding: { status: string } | null;
    jobs: ReadonlyArray<unknown>;
  },
  elapsedMs: number,
  maxElapsedMs = INTEGRATION_POLLING_MAX_DURATION_MS,
): boolean => {
  if (elapsedMs >= maxElapsedMs) return false;
  if (response.understanding?.status === 'failed') return false;
  return (
    response.understanding?.status === 'analyzing' ||
    !response.understanding ||
    (response.understanding.status === 'ready' && response.jobs.length === 0)
  );
};

export const integrationPollingInterval = (
  elapsedMs: number,
  earlyWindowMs = INTEGRATION_POLLING_EARLY_WINDOW_MS,
): number =>
  elapsedMs < earlyWindowMs
    ? INTEGRATION_POLLING_INITIAL_INTERVAL_MS
    : INTEGRATION_POLLING_INTERVAL_MS;

export const nextJobActiveState = (
  routines: ReadonlyArray<Pick<RoutineForJobState, 'id' | 'active'>>,
  routineState: Readonly<Record<string, boolean>>,
): boolean => !isJobOn(routines, routineState);

export const cascadeJobRoutineState = (
  routines: ReadonlyArray<Pick<RoutineForJobState, 'id'>>,
  active: boolean,
): Record<string, boolean> =>
  routines.reduce<Record<string, boolean>>((state, routine) => {
    state[routine.id] = active;
    return state;
  }, {});

export const isJobCustomised = (
  job: JobForCustomisation,
  routines: ReadonlyArray<RoutineForJobState>,
): boolean => {
  if (!routines.some((routine) => routine.active)) return false;
  return (
    job.customized ||
    job.activeGeneration > 1 ||
    routines.some((routine) => {
      return (
        !routine.active ||
        (routine.originalSchedule != null &&
          !sameSchedule(routine.schedule, routine.originalSchedule))
      );
    })
  );
};
